import React, { useCallback, useMemo } from 'react';
import { isArray, isNull } from 'lodash/fp';
import classnames from 'classnames';
import { isEqual } from 'lodash';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { getDocumentFields, PortfolioQuestion, PortfolioQuestionResponse, redirectToDocument } from '../store';
import { DatasetFieldType, DatasetType, OpenFieldType } from '../../../datasets/store/types';
import styles from '../PortfolioAnalytics.module.scss';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { OverflowTooltip } from '../../../shared/tooltip';
import { SearchField } from '../../../datasets/instances/store';
import { IconButton } from '../../../shared/button/IconButton';
import { ArrowRight } from '../../../shared/icons';
import { CalculatorField } from '../../calculator/store';
import { ReadOnlyCalculatorField } from '../../../datasets/fields/ReadOnlyCalculatorField';
import { GraphKeyAndColour } from '../../../shared/analytics/PieChart';

interface PortfolioAnalyticsTableProps {
    selectedFieldLabel: string;
    askedPortfolioQuestion: PortfolioQuestion;
    portfolioResponse: PortfolioQuestionResponse | null;
    selectedPieKey?: string;
    graphColours: GraphKeyAndColour;
}

export const PortfolioAnalyticsTable: React.FC<PortfolioAnalyticsTableProps> = ({ selectedFieldLabel, askedPortfolioQuestion, portfolioResponse, selectedPieKey, graphColours }) => {
    const dispatch = useAppDispatch();
    const { fieldId, datasetType, fieldType } = askedPortfolioQuestion;

    const availableFields = useAppSelector(getDocumentFields);

    const responseBreakdown = useMemo(() => !isNull(portfolioResponse) ? portfolioResponse.results : [], [portfolioResponse]);

    const goToDocument = useCallback((documentId: number, datasetInstanceId: number) => {
        const { sectionId, id, datasetId, groupFieldIndex, parentFieldId, type } = availableFields.find(({ id }) => id === fieldId)!;
        const fieldToOpen: SearchField = { sectionId, fieldId: id, datasetId: datasetId.toString(), groupIndex: groupFieldIndex, type: OpenFieldType.FIELD, parentFieldId, label: selectedFieldLabel, fieldType: type };
        dispatch(redirectToDocument(documentId, datasetInstanceId, fieldToOpen));
    }, [dispatch, fieldId, availableFields, selectedFieldLabel]);

    const parseCalculatorResponse = useCallback((value: string) => {
        try {
            return JSON.parse(value) as CalculatorField[];
        }
        catch (e) {
            return [];
        }
    }, []);

    const getFormattedCalculatorValue = useCallback((value: string, rowIdentifier?: string, showRowDivider?: boolean) => {
        if (value.includes('No Value Provided')) {
            return (
                <div className={classnames(styles.formValue, { [styles.tableRowValue]: rowIdentifier, [styles.noValueProvided]: true, [styles.rowDivider]: showRowDivider })}>
                    {rowIdentifier && <OverflowTooltip overlayText={rowIdentifier} className={styles.rowIdentifier} />}
                    {value}
                </div>
            );
        } else {
            const calculatorField = parseCalculatorResponse(value);
            return (
                <div className={classnames(styles.formValue, { [styles.tableRowValue]: rowIdentifier, [styles.rowDivider]: showRowDivider })}>
                    {rowIdentifier && <OverflowTooltip overlayText={rowIdentifier} className={styles.rowIdentifier} />}
                    {calculatorField.length > 0 ? calculatorField.map(({ calculation, calculationCondition, output, gracePeriod }, calculationIndex) => {
                        return (
                            <ReadOnlyCalculatorField
                                value={calculatorField}
                                calculation={calculation}
                                output={output}
                                gracePeriod={gracePeriod}
                                calculationCondition={calculationCondition}
                                calculationIndex={calculationIndex}
                                borderColor={'grey'}
                                key={calculationIndex}
                                showOutputField={false}
                                showGracePeriod={false}
                            />
                        );
                    }) :
                        <div className={classnames(styles.formValue, { [styles.tableRowValue]: rowIdentifier, [styles.noValueProvided]: true, [styles.rowDivider]: showRowDivider })}>
                            {rowIdentifier && <OverflowTooltip overlayText={rowIdentifier} className={styles.rowIdentifier} />}
                            There was an error parsing the value, please ask the question again.
                        </div>
                    }
                </div>
            );
        }
    }, [parseCalculatorResponse]);

    const getFormattedFieldValue = useCallback((value: string | string[][]) => {
        const isForm = datasetType === DatasetType.FORM;
        if (isNull(value)) {
            return null;
        }
        if (isForm) {
            let formattedValue = isArray(value) ? value.join(', ') : value;
            if (fieldType === DatasetFieldType.CALCULATOR) {
                return getFormattedCalculatorValue(formattedValue);
            }
            return <div className={classnames(styles.formValue, { [styles.noValueProvided]: formattedValue === 'No Value Provided' })}>{formattedValue}</div>;
        } else {
            const rowValues = isArray(value) ? value : [[value]];
            return (
                <div className={styles.tableValueWrapper}>
                    {rowValues.map((rowValue, index) => {
                        const showRowDivider = index !== (rowValues.length - 1);
                        const [rowId, value] = rowValue;
                        if (fieldType === DatasetFieldType.CALCULATOR) {
                            return getFormattedCalculatorValue(value, rowId, showRowDivider);
                        }
                        return (
                            <div className={classnames(styles.tableRowValue, { [styles.noValueProvided]: rowValue.includes('No Value Provided'), [styles.rowDivider]: showRowDivider })} key={index}>
                                {rowId && <OverflowTooltip overlayText={rowId} className={styles.rowIdentifier} />}
                                {value}
                            </div>
                        );
                    })}
                </div>
            );
        }
    }, [datasetType, fieldType, getFormattedCalculatorValue]);

    const getFormattedPieKeys = useCallback((graphKey: string | string[]) => {
        const isForm = datasetType === DatasetType.FORM;
        const rowsHaveSameKey = isArray(graphKey) ? graphKey.every(key => isEqual(key, graphKey[0])) : false;
        if (isForm || rowsHaveSameKey) {
            const key = isArray(graphKey) ? graphKey[0] : graphKey;
            return (
                <div className={styles.formPieSegmentWrapper}>
                    <div className={styles.pieSegmentColour} style={{ backgroundColor: graphColours[key] }} />
                </div>
            );
        }
        const rowPieKeys = graphKey as string[];
        return (
            <div className={styles.tablePieSegmentWrapper}>
                {rowPieKeys.map((graphKey, index) => (
                    <div key={index} className={styles.pieSegmentColour} style={{ backgroundColor: graphColours[graphKey] }} />
                ))}
            </div>
        );
    }, [datasetType, graphColours]);

    const getPieKeyIsSelected = useCallback((graphKey: string | string[]) => selectedPieKey && isArray(graphKey) ? graphKey.includes(selectedPieKey) : selectedPieKey === graphKey, [selectedPieKey]);

    const tableValues = useMemo(() => (
        responseBreakdown.map(({ value, documentDescription, documentId, datasetInstanceId, partyA, partyB, graphKey }, index) => (
            <div key={index} className={classnames(styles.tableRow, { [styles.selectedRow]: getPieKeyIsSelected(graphKey) })}>
                {getFormattedPieKeys(graphKey)}
                {getFormattedFieldValue(value)}
                <div className={styles.documentNameWrapper}>
                    <OverflowTooltip overlayText={documentDescription} className={styles.documentName} />
                </div>
                <div className={styles.partyWrapper}>
                    <OverflowTooltip overlayText={partyA} className={styles.partyName} />
                </div>
                <div className={styles.partyWrapper}>
                    <OverflowTooltip overlayText={partyB} className={styles.partyName} />
                </div>
                <div className={styles.documentLink}>
                    <IconButton icon={ArrowRight} onClick={() => goToDocument(documentId, datasetInstanceId)} />
                </div>
            </div>
        ))
    ), [responseBreakdown, getFormattedFieldValue, goToDocument, getFormattedPieKeys, getPieKeyIsSelected]);

    return (
        <div className={styles.breakdownWrapper}>
            <div className={styles.tableWrapper}>
                <div className={styles.tableHeader}>
                    <div className={styles.pieColourHeader} />
                    <div className={styles.fieldHeader}>
                        <div className={styles.headerLabel}>{selectedFieldLabel}</div>
                    </div>
                    <div className={styles.documentHeader}>
                        <div className={styles.headerLabel}>Document</div>
                    </div>
                    <div className={styles.partyHeader}>
                        <div className={styles.headerLabel}>Party A</div>
                    </div>
                    <div className={styles.partyHeader}>
                        <div className={styles.headerLabel}>Party B</div>
                    </div>
                    <div className={styles.linkHeader}>
                        <div className={styles.headerLabel}>Link</div>
                    </div>
                </div>
                <div className={styles.scrollableWrapper}>
                    <Scrollable>
                        <div className={styles.tableResults}>
                            {tableValues}
                        </div>
                    </Scrollable>
                </div>
            </div>
            <div className={styles.total}>Total: {responseBreakdown.length}</div>
        </div>
    );
};

